/**
 * @file src/audio/audioContextBoot.js
 * @description Decides the ONE audio context the app runs on, with the requested
 * `latencyHint` applied at construction.
 *
 * Chrome (and every other engine) reads `latencyHint` only in the `AudioContext`
 * constructor — it sizes the device callback once and never re-reads the value.
 * Tone 15 makes that explicit: `Context.latencyHint` is a getter with no setter, and
 * `rawContext` is a standardized-audio-context wrapper that has none either. So the
 * hint has exactly one moment to land, and assigning it afterwards — which is what
 * used to happen — is silently a no-op.
 *
 * The wrinkle is that we cannot get in front of Tone. Importing the `tone` package
 * runs `export const Destination = getContext().destination` at module scope (and the
 * same for `Transport`, `Master`, `Listener`, `Draw`), and `getContext()` BUILDS the
 * global context when none is set. No amount of import ordering wins that race in a
 * bundled build: the app's Tone vendor chunk is a static import of the entry chunk, so
 * it is fully evaluated before the first line of app code runs. Deep-importing Tone's
 * side-effect-free modules works in the unbundled dev server and silently loses in
 * production — the worst possible split.
 *
 * So this does not race. It lets Tone build its default, then replaces it with one
 * carrying the right hint and disposes the original — which also closes it, so no
 * device context is left behind. When the hint we want is Tone's own default there is
 * nothing to replace and we keep what is already there.
 *
 * The cost of replacing is that Tone's five deprecated module-scope singletons stay
 * bound to the disposed context. Nothing may use them; `getDestination()`,
 * `getTransport()` and friends resolve the live context on every call and are the only
 * safe form. An ESLint rule pins that down.
 *
 * @memberof AudioModule
 */
import { Context, getContext, setContext, getDestination } from 'tone';
import { resolveAudioPerformancePreset } from '../config/audioPerformance.js';
import { noteAudioContextBaseLatencyMs } from '../config/audioOffset.js';

/**
 * @typedef {object} AudioContextInfo
 * @property {string} latencyHint the hint the context was built with
 * @property {'url'|'cookie'|'preset'} source who chose the BUFFER: the url, the website's cookie, or
 *   nobody — `preset` meaning the quality preset's own hint stands
 * @property {number|null} sampleRate
 * @property {number|null} baseLatencyMs one device callback, as the browser reports it
 * @property {number|null} callbackFrames `baseLatencyMs` expressed in frames
 * @property {boolean} replacedToneDefault whether Tone's own context had to be swapped out
 */

/** @type {AudioContextInfo|null} */
let installedInfo = null;

/**
 * Frames per device callback, derived from the context's own reported latency.
 * Chrome reports `baseLatency` as one device buffer, so reading it back is the cheap
 * way to confirm the hint changed the callback size without a tracing session.
 * @param {{ baseLatency?: number, sampleRate?: number }} raw
 * @returns {number|null}
 */
function estimateCallbackFrames(raw) {
  const baseLatency = Number(raw?.baseLatency);
  const sampleRate = Number(raw?.sampleRate);
  if (!Number.isFinite(baseLatency) || !Number.isFinite(sampleRate) || sampleRate <= 0) return null;
  return Math.round(baseLatency * sampleRate);
}

/**
 * Resolve the hint this session should run on, from `?audioBuffer=`, the website's cookie, or the
 * quality preset that won.
 * @returns {{ latencyHint: string, source: 'url'|'cookie'|'preset' }}
 */
function resolveRequestedHint() {
  try {
    const resolution = resolveAudioPerformancePreset(window.location?.search ?? '');
    return {
      latencyHint: resolution?.preset?.audioContextLatencyHint || 'interactive',
      // The BUFFER's provenance, not the quality preset's. They are resolved independently, and
      // when someone is chasing "why is my buffer not what I chose" the answer is whether the URL
      // or the cookie answered — `preset` meaning neither did, so the quality preset's own hint
      // stands.
      source: resolution?.bufferSource ?? 'preset',
    };
  } catch (error) {
    console.warn('[audio] could not resolve the audio performance preset; keeping the default buffer', error);
    return { latencyHint: 'interactive', source: 'preset' };
  }
}

/**
 * Put the app on a context built with the requested buffer size. Idempotent.
 * @returns {AudioContextInfo|null}
 */
export function installAudioContext() {
  if (installedInfo) return installedInfo;
  if (typeof window === 'undefined') return null;

  const { latencyHint, source } = resolveRequestedHint();

  try {
    const current = getContext();
    let replacedToneDefault = false;
    let context = current;

    if (current?.latencyHint !== latencyHint) {
      context = new Context({ latencyHint });
      // `true` disposes the context we are replacing, which closes it — otherwise the
      // page keeps a second, permanently suspended device context for the whole session.
      setContext(context, true);
      replacedToneDefault = true;
    }

    const raw = context?.rawContext ?? null;
    installedInfo = {
      latencyHint: context?.latencyHint ?? latencyHint,
      source,
      sampleRate: Number(raw?.sampleRate) || null,
      baseLatencyMs: Number.isFinite(Number(raw?.baseLatency)) ? Number(raw.baseLatency) * 1000 : null,
      callbackFrames: estimateCallbackFrames(raw),
      replacedToneDefault,
    };

    // Hand the manual audio offset both halves: the MEASURED callback length, and WHICH buffer was
    // asked for. It needs the second to tell a buffer change apart from someone plugging in
    // different headphones. Pushed from here because the offset module must not import this one —
    // importing it installs a context.
    noteAudioContextBaseLatencyMs(installedInfo.baseLatencyMs, installedInfo.latencyHint);

    // The failure this guards against is silent and total: if anything still reaches the
    // old context, every node connection crosses contexts and the app has no audio at
    // all. Assert against the destination specifically, since that is what the terminal
    // output connects to.
    const destinationContext = getDestination()?.context;
    if (destinationContext !== context) {
      console.error(
        '[audio] the master output is on a different context than the one that was installed; ' +
        'audio will fail to connect. Something is holding one of Tone\'s module-scope singletons.',
      );
    }

    return installedInfo;
  } catch (error) {
    console.error('[audio] failed to install the audio context with the requested buffer size', error);
    return null;
  }
}

/**
 * What the context was actually built with, or null if the install never ran. This is the
 * inspection seam — no `window.*` handle, per the no-globals decision. To read the realised
 * buffer size at the dev console:
 *
 *   (await import('/src/audio/audioContextBoot.js')).getInstalledAudioContextInfo()
 *
 * @returns {AudioContextInfo|null}
 */
export function getInstalledAudioContextInfo() {
  return installedInfo ? { ...installedInfo } : null;
}

// Side effect on import. Ordering no longer decides correctness, but running before the
// app builds any node still avoids disposing a context something is already wired to.
installAudioContext();
